import { Box, Card, Chip, InputLabel, MenuItem, Select, Typography, FormControl } from "@mui/material"
import { Datagrid, FunctionField, List, useListContext, useTranslate } from "react-admin"
import { EmptyContent } from "src/components/empty-content"
import XTextField from "../../components/XTextField"
import XCurrencyField from "../../components/XCurrencyField"
import { useState } from "react"
import XNumberField from "../../components/XNumberField"
import XChainField from "../../components/XChainField"
import XDateField from "../../components/XDateField"
import { chains } from "src/assets/data/chains"
import XToken from "../../components/XToken"
interface Props {
  record: any;
  transactionType?: string | undefined;
  asset?: string | undefined;
  isDialog: boolean
}

const TYPES_MAP: Record<string, string[]> = {
  deposit: ['Deposit','DepositFutureExternal','DepositPredictExternal'],
  withdraw: ['Withdraw','WithdrawFutureExternal','WithdrawPredictExternal'],
  transfer: ['Swap', 'DepositFuture', 'WithdrawFuture'],
  others: ['Other'],
  all_types: []
}

const CHAIN_MAP: Record<string, number[]> = {
  all_assets: [],
  bnb_meme: [56],
  mon_meme: [143],
  sol_meme: [501424],
  usdc_perps: [42161],
  usdc_prediction: [],
  dex: [42161],
  meme: [56, 143, 501424]
}

const TOKEN_MAP: Record<string, string[]> = {
  all_assets: [],
  bnb_meme: ["0x0000000000000000000000000000000000000000"],
  mon_meme: ["0x0000000000000000000000000000000000000000"],
  sol_meme: ["So11111111111111111111111111111111111111111"],
  usdc_perps: ["0xaf88d065e77c8cc2239327c5edb3a432268e5831"],
  usdc_prediction: ["0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174"],
  dex: ["0xaf88d065e77c8cc2239327c5edb3a432268e5831"],
  meme: ["0x0000000000000000000000000000000000000000","So11111111111111111111111111111111111111111"]
}

const CustomFilters = () => {
  const t = useTranslate();
  const {setFilters} = useListContext();
  const [selectedType, setSelectedType] = useState('all_types');
  const [selectedAsset, setSelectedAsset] = useState('all_assets');

  const applyFilters = (type: string, asset: string) => {
    const chainId = CHAIN_MAP[asset] ?? [];
    const token = TOKEN_MAP[asset];
    const queryTypes = TYPES_MAP[type]
    setFilters({type: queryTypes, chainId: chainId, token: token})
  }

  const handleTypeChange = (value: string) => {
    setSelectedType(value);
    applyFilters(value, selectedAsset);
  }

  const handleAssetChange = (value: string) => {
    setSelectedAsset(value);
    applyFilters(selectedType, value)
  }

  return (
    <Box sx={{display: 'flex', gap: 2, alignItems: 'center'}}>
      <FormControl size="small" sx={{minWidth: 140}}>
        <InputLabel shrink>Type</InputLabel>
        <Select value={selectedType} label={t('module.userInfo.transferHistory.type')} onChange={(e) => handleTypeChange(e.target.value)} displayEmpty>
          <MenuItem value="all_types">{t('module.userInfo.transferHistory.types.all')}</MenuItem>
          <MenuItem value="deposit">{t('module.userInfo.transferHistory.types.deposit')}</MenuItem>
          <MenuItem value="withdraw">{t('module.userInfo.transferHistory.types.withdraw')}</MenuItem>
          <MenuItem value="transfer">{t('module.userInfo.transferHistory.types.transfer')}</MenuItem>
          <MenuItem value="others">{t('module.userInfo.transferHistory.types.others')}</MenuItem>
        </Select>
      </FormControl>
      <FormControl size="small" sx={{minWidth: 140}}>
        <InputLabel shrink>Asset</InputLabel>
        <Select value={selectedAsset} label={t('module.userInfo.transferHistory.asset')} onChange={(e) => handleAssetChange(e.target.value)} displayEmpty>
          <MenuItem value="all_assets">{t('module.userInfo.transferHistory.assets.all')}</MenuItem>
          <MenuItem value="bnb_meme">{t('module.userInfo.transferHistory.assets.bnbMeme')}</MenuItem>
          <MenuItem value="mon_meme">{t('module.userInfo.transferHistory.assets.monMeme')}</MenuItem>
          <MenuItem value="sol_meme">{t('module.userInfo.transferHistory.assets.solMeme')}</MenuItem>
          <MenuItem value="usdc_perps">{t('module.userInfo.transferHistory.assets.usdcPerps')}</MenuItem>
          <MenuItem value="usdc_prediction">{t('module.userInfo.transferHistory.assets.usdcPrediction')}</MenuItem>
          {/* <MenuItem value="dex">{t('module.userInfo.transferHistory.assets.dex')}</MenuItem>
          <MenuItem value="meme">{t('module.userInfo.transferHistory.assets.meme')}</MenuItem> */}
        </Select>
      </FormControl>
    </Box>
  )
}

const TransferTable = ({ record, transactionType, asset , isDialog }: Props) => {
  const t = useTranslate();
  const allWallets = record?.data?.wallets.map((w: any) => w.walletAddress);
  const chainId = asset ? CHAIN_MAP[asset] : []
  const token = asset ? TOKEN_MAP[asset] : []
  const queryTypes = transactionType ? TYPES_MAP[transactionType] : []

  return (
    <Card sx={{ mt: 3, mb: 3 }}>
      {!isDialog && <Typography variant="h6" sx={{ p: 3, pb: 0 }}>
        {t('module.userInfo.transferHistory.title')}
      </Typography>}
      <List
        resource={`user-management/user-info/transfer-history`}
        filter={isDialog ? {walletAddresses: allWallets, chainId: chainId, type: queryTypes, token: token} : {walletAddresses: allWallets}}
        disableSyncWithLocation
        empty={<EmptyContent />}
        perPage={isDialog ? 10 : 5}
        actions={false}
        queryOptions={{enabled: !!allWallets?.length}}
        sort={{ field: "createdAt", order: "DESC" }}>
        {!isDialog && <Box sx={{ p: 2 }}>
          <CustomFilters />
        </Box>}
        <Box sx={{ overflow: 'auto' }}>
          <Datagrid bulkActionButtons={false} sx={{ '& .MuiTableCell-root': { whiteSpace: 'nowrap' } }} empty={<EmptyContent />}>
            <FunctionField label={t('module.userInfo.transferHistory.type')} source="type" render={(r: any) => (<XTextField copy={false} text={r.type} />)} />
            <FunctionField label={t('module.userInfo.transferHistory.status')} source="status" render={(r: any) => (<Chip label={r.status} color={r.status === 'Success' ? 'success' : 'error'} size="small" />)} />
            <FunctionField label={t('module.userInfo.transferHistory.token')} source="token" render={(r: any) => {
              if (r.type === 'Swap') {
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '80px' }}>
                    <XToken chainId={r.chainId} tokenAddress={r.token}/>
                    <Typography color="textSecondary">→</Typography>
                    <XToken chainId={r.toChainId} tokenAddress={r.toToken}/>
                  </Box>
                )
              }
              return <XToken tokenAddress={r.token} chainId={r.chainId} />;
            }} />
            <FunctionField label={t('module.userInfo.transferHistory.amount')} source="amount" render={(r: any) => {
              if (r.type === 'Swap') {
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <XNumberField number={parseFloat(r.amount)} />
                    <Typography color="textSecondary">→</Typography>
                    {r.toAmount ? <XNumberField number={parseFloat(r.toAmount)} /> : <Typography>-</Typography>}
                  </Box>
                )
              }
              return <XNumberField number={parseFloat(r.amount)} />;
            }} />
            <FunctionField label={t('module.userInfo.transferHistory.amountUsd')} source="amountUsd" sortable render={(r: any) => (<XCurrencyField value={parseFloat(r.amountUsd)} currency={{ symbol: '$', position: 'before' }} />)} />
            <FunctionField label={t('module.userInfo.transferHistory.chainId')} source="chainId" render={(r: any) => {
              if (r.type === 'Swap') {
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '80px' }}>
                    <XChainField value={r.chainId} />
                    <Typography color="textSecondary">→</Typography>
                    {r.toChainId ? <XChainField value={r.toChainId} /> : <Typography>-</Typography>}
                  </Box>
                )
              }
              return <XChainField value={r.chainId} />;
            }} />
            <FunctionField label={t('module.userInfo.transferHistory.from')} source="from" render={(r: any) => <XTextField text={r.from} short />} />
            <FunctionField label={t('module.userInfo.transferHistory.to')} source="to" render={(r: any) => <XTextField text={r.to} short />} />
            <FunctionField label={t('module.userInfo.transferHistory.txHash')} source="txHash" render={(r: any) => {
              const chainInfo = chains.find(c => c.id === r.chainId);
              const txHashLink = r.route === 'Relay'
                ? `https://relay.link/transaction/${r.txHash}`
                : chainInfo?.browser
                  ? `${chainInfo.browser}${r.txHash}`
                  : undefined;
              return (
                <Box
                  onClick={() => { txHashLink && window.open(txHashLink, '_blank') }}
                  sx={{ cursor: txHashLink ? 'pointer' : 'default', color: txHashLink ? 'primary.main' : 'inherit' }}
                >
                  <XTextField text={r.txHash} short copy={false} />
                </Box>
              )
            }} />
            <FunctionField label={t('module.userInfo.transferHistory.createdAt')} source="createdAt" sortable render={(r: any) => <XDateField value={r.createdAt} fullDate />} />
          </Datagrid>
        </Box>
      </List>
    </Card>
  )
}

export default TransferTable